import { RadioGroup as ChakraRadioGroup } from "@chakra-ui/react";
import type { ComponentProps } from "react";
import { forwardRef } from "react";
import type { RadioSharedProps } from "./Radio.props";

type ChakraRadioItemProps = ComponentProps<typeof ChakraRadioGroup.Item>;
type ChakraItemIndicatorProps = ComponentProps<
  typeof ChakraRadioGroup.ItemIndicator
>;

const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  {
    children,
    tabIndex,
    isDisabled,
    disabled,
    value,
    inputProps,
    indicatorProps,
    ...props
  },
  ref
): JSX.Element {
  const disabledValue = disabled !== undefined ? disabled : isDisabled;
  const valueString = value !== undefined ? String(value) : "0";

  return (
    <ChakraRadioGroup.Item
      value={valueString}
      disabled={disabledValue}
      colorPalette="primary"
      cursor="pointer"
      {...props}
    >
      <ChakraRadioGroup.ItemHiddenInput
        ref={ref}
        {...inputProps}
        {...(tabIndex != null ? { tabIndex } : {})}
      />
      <ChakraRadioGroup.ItemIndicator
        css={{
          "&[data-state=checked]": {
            backgroundColor: "primary.500",
            borderColor: "primary.500",
          },
        }}
        {...indicatorProps}
      />
      {children != null && (
        <ChakraRadioGroup.ItemText color="fg">
          {children}
        </ChakraRadioGroup.ItemText>
      )}
    </ChakraRadioGroup.Item>
  );
});

export default Radio;

export type RadioProps = RadioSharedProps &
  Omit<ChakraRadioItemProps, "value" | "disabled" | "children"> & {
    value?: string | number;
    isDisabled?: boolean;
    disabled?: boolean;
    tabIndex?: number;
    inputProps?: ComponentProps<typeof ChakraRadioGroup.ItemHiddenInput>;
    indicatorProps?: ChakraItemIndicatorProps;
  };
